<template>
  <!-- Popover flotante del gesto de avance inline: al soltar el arrastre, el
       usuario elige estado + fecha para el delta marcado. Posición fija anclada
       al punto de soltado (flip/clamp para no salirse del viewport). -->
  <div
    v-if="visible"
    class="inline-advance-pop fixed z-[60] w-[280px] rounded-xl border border-[#c9d6ef] bg-white shadow-xl"
    :style="{ left: adjusted.x + 'px', top: adjusted.y + 'px' }"
    @pointerdown.stop
  >
    <!-- Cabecera: delta marcado + sentido. -->
    <div class="flex items-center justify-between px-3.5 py-2.5 border-b border-[#eef0f4]">
      <div class="flex items-center gap-2">
        <span class="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#eef3fb] text-[#3a6bc8]">
          <b-icon pack="fas" icon="bolt" size="is-small" />
        </span>
        <div class="leading-tight">
          <div class="text-[13px] font-semibold text-gray-800">+{{ deltaMeters.toFixed(2) }} m</div>
          <div class="text-[11px] text-gray-400">{{ direction === 'l2r' ? 'A → B' : 'B → A' }} · {{ totalMeters.toFixed(2) }} / {{ totalLength.toFixed(2) }} m</div>
        </div>
      </div>
      <button type="button" class="text-gray-400 hover:text-gray-600" @click="_Cancel">
        <b-icon pack="fas" icon="xmark" size="is-small" />
      </button>
    </div>

    <!-- Cuerpo: estado + fecha. -->
    <div class="px-3.5 py-3 space-y-3">
      <div>
        <label class="block text-[10px] uppercase text-gray-500 font-semibold mb-1.5">{{ $t('status') }}</label>
        <multiselect
          v-model="selectedStatus"
          :options="statuses"
          track-by="value"
          label="label"
          :placeholder="$t('select_status')"
          :show-labels="false"
          :allow-empty="true"
          :max-height="180"
          open-direction="bottom"
          class="inline-advance-status-select"
        >
          <template #option="{ option }">
            <div class="flex items-center gap-2">
              <span class="status-swatch" :style="{ backgroundColor: option.color }"></span>
              <span>{{ option.label }}</span>
            </div>
          </template>
          <template #singleLabel="{ option }">
            <div class="flex items-center gap-2">
              <span class="status-swatch" :style="{ backgroundColor: option.color }"></span>
              <span>{{ option.label }}</span>
            </div>
          </template>
          <template #noOptions>{{ $t('no_options_available') }}</template>
        </multiselect>
      </div>

      <div>
        <label class="block text-[10px] uppercase text-gray-500 font-semibold mb-1.5">{{ $t('registration_date') }}</label>
        <b-datepicker
          v-model="startDate"
          :max-date="today"
          position="is-top-right"
          icon="calendar-day"
          icon-pack="fas"
          size="is-small"
          append-to-body
          editable
        />
      </div>

      <p v-if="errorMsg" class="text-[12px] text-[#e02424] flex items-start gap-1">
        <b-icon pack="fas" icon="triangle-exclamation" size="is-small" />
        <span>{{ errorMsg }}</span>
      </p>
    </div>

    <!-- Pie: cancelar / aplicar. -->
    <div class="flex items-center justify-end gap-2 px-3.5 py-2.5 border-t border-[#eef0f4]">
      <button
        type="button"
        class="px-3 py-1.5 rounded-lg text-[13px] font-medium text-gray-600 hover:bg-gray-100"
        @click="_Cancel"
      >{{ $t('cancel') }}</button>
      <button
        type="button"
        class="px-3.5 py-1.5 rounded-lg text-[13px] font-semibold text-white bg-[#3a6bc8] hover:bg-[#2f5aad] disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canApply"
        @click="_Apply"
      >
        <b-icon v-if="loading" pack="fas" icon="spinner" size="is-small" custom-class="fa-spin" />
        <span v-else>{{ $t('apply') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'

/**
 * Popover de estado + fecha para el gesto de avance inline. Presentación pura: el
 * host (CadStudio) le pasa los estados ELEGIBLES (ya marcados con $isDisabled por
 * advance-eligibility.helper), el delta marcado y la posición; el popover solo
 * recoge estado + fecha y emite `epc-apply`. La persistencia vive en el host.
 */
export default {
  name: 'InlineAdvancePopover',

  components: { Multiselect },

  props: {
    visible:     { type: Boolean, default: false },
    // Punto de soltado en coords de viewport.
    position:    { type: Object,  default: () => ({ x: 0, y: 0 }) },
    // Estados de la capa con $isDisabled según elegibilidad.
    statuses:    { type: Array,   default: () => [] },
    direction:   { type: String,  default: 'l2r' },
    deltaMeters: { type: Number,  default: 0 },
    totalMeters: { type: Number,  default: 0 },
    totalLength: { type: Number,  default: 0 },
    // Valor de estado a pre-seleccionar al abrir (auto-objetivo multi-estado).
    preselect:   { default: null },
    loading:     { type: Boolean, default: false },
    errorMsg:    { type: String,  default: '' },
  },

  data() {
    return {
      selectedStatus: null,
      startDate:      new Date(),
      popoverWidth:   280,
      popoverHeight:  260,
      windowWidth:    typeof window !== 'undefined' ? window.innerWidth  : 1280,
      windowHeight:   typeof window !== 'undefined' ? window.innerHeight : 800,
    }
  },

  computed: {
    today() {
      return new Date()
    },

    canApply() {
      return !this.loading && !!this.selectedStatus && this.deltaMeters > 1e-4
    },

    /** Mantiene el popover dentro del viewport (flip horizontal/vertical + clamp). */
    adjusted() {
      const margin = 10
      const GAP = 14
      let x = this.position.x + GAP
      let y = this.position.y + GAP
      if (x + this.popoverWidth > this.windowWidth - margin) {
        x = this.position.x - this.popoverWidth - GAP
      }
      if (x < margin) x = margin
      if (y + this.popoverHeight > this.windowHeight - margin) {
        y = this.position.y - this.popoverHeight - GAP
      }
      if (y < margin) y = margin
      return { x, y }
    },
  },

  watch: {
    visible(open) {
      if (open) {
        // Reset al abrir: fecha = hoy; estado = el pre-seleccionado (auto-objetivo)
        // si es elegible, si no, ninguno. El usuario puede cambiarlo.
        this.startDate = new Date()
        this.windowWidth  = window.innerWidth
        this.windowHeight = window.innerHeight
        this.selectedStatus = this._ResolvePreselect()
      }
    },
    // Si cambia el objetivo/estados mientras está abierto (re-marcar), re-aplicar.
    preselect() { if (this.visible) this.selectedStatus = this._ResolvePreselect() },
  },

  methods: {
    /** Opción pre-seleccionada (auto-objetivo) si existe y es elegible; si no, null. */
    _ResolvePreselect() {
      if (this.preselect == null) return null
      const opt = this.statuses.find(s => s.value === this.preselect && !s.$isDisabled)
      return opt || null
    },

    _Apply() {
      if (!this.canApply) return
      this.$emit('epc-apply', {
        status:      this.selectedStatus.value,
        statusLabel: this.selectedStatus.label,
        statusColor: this.selectedStatus.color,
        startDate:   this._FormatYMD(this.startDate),
      })
    },

    _Cancel() {
      this.$emit('epc-cancel')
    },

    /** Fecha → 'YYYY-MM-DD' (mismo formato que el modal). */
    _FormatYMD(d) {
      if (!(d instanceof Date) || isNaN(d)) return null
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
  },
}
</script>

<style scoped>
.status-swatch {
  width: 11px;
  height: 11px;
  border-radius: 3px;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Multiselect compacto, acorde al popover. */
.inline-advance-status-select :deep(.multiselect__tags) {
  min-height: 34px;
  padding: 5px 40px 0 8px;
  border-radius: 8px;
  border-color: #e9e9ef;
  font-size: 13px;
}
.inline-advance-status-select :deep(.multiselect__option--highlight) {
  background: #3a6bc8;
}
.inline-advance-status-select :deep(.multiselect__option--highlight::after) {
  background: #3a6bc8;
}
.inline-advance-status-select :deep(.multiselect__option--disabled) {
  background: #f7f7fa;
  color: #c3c3cc;
}
</style>
